// App
import type { ComponentType, FC, HTMLAttributes, Key, ReactElement } from 'react';

// Misc / utils
import * as S from './styles';

const SLIDER_LEGEND: Record<number, string> = {
  0: 'Any',
  1: '1 hour',
  2: '45 min',
  3: '30 min',
  4: '<15 min',
};

interface CommuteSliderProps {
  handleSliderSelection: (value: number) => void;
  currentValue: number;
}

interface ThumbProps extends HTMLAttributes<HTMLDivElement> {
  key?: Key | null;
}

interface TrackProps extends HTMLAttributes<HTMLDivElement> {
  key?: Key | null;
}

interface TrackState {
  index: number;
  value: number | ReadonlyArray<number>;
}

interface MarkProps extends HTMLAttributes<HTMLSpanElement> {
  key?: Key | null;
}

interface SliderTrackComponentProps {
  index: number;
  [key: string]: unknown;
}

const CommuteSlider: FC<CommuteSliderProps> = ({ handleSliderSelection, currentValue }) => {
  const Thumb = (props: ThumbProps): ReactElement => <S.SliderThumb {...props}>{'| |'}</S.SliderThumb>;
  const Track = (props: TrackProps, state: TrackState): ReactElement => {
    const SliderTrackComponent = S.SliderTrack as ComponentType<SliderTrackComponentProps>;
    return <SliderTrackComponent {...props} index={state.index} />;
  };
  const Marks = (props: MarkProps): ReactElement => {
    const currentKey = Number(props.key);

    // This is only to get the labels underneath the slider to line up
    if (currentKey >= 1 && props.style) {
      const currentPos = (props.style.left as string) || '0';
      const numericPos = typeof currentPos === 'string' ? parseFloat(currentPos) : currentPos;
      const newPos = numericPos - 10;

      props.style = {
        ...props.style,
        left: `${newPos}px`,
        width: '50px',
      };
    }

    return <S.SliderMarks {...props}>{SLIDER_LEGEND[currentKey]}</S.SliderMarks>;
  };

  const handleChange = (val: number | ReadonlyArray<number>) => {
    const value = Array.isArray(val) ? val[0] : val;
    handleSliderSelection(value);
  };

  return (
    <S.CommuteSlider
      ariaLabel={'Desired maximum commute time'}
      ariaValuetext={(state) => `Selected maximum commute time ${SLIDER_LEGEND[state.valueNow]}`}
      marks
      min={0}
      max={4}
      value={currentValue}
      renderMark={Marks}
      renderThumb={Thumb}
      renderTrack={Track}
      onAfterChange={handleChange}
    />
  );
};

export default CommuteSlider;
